import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { ArrowBack, CheckCircle, AccessTime, People, TrendingUp } from '@mui/icons-material';
import {
    BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import BarberBottomNav from './BarberBottomNav';
import { colors, typography, radius, glassCard } from '../../theme';

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.3 }}>{label}</Typography>
            {payload.map((p, i) => <Typography key={i} sx={{ fontSize: 12, color: p.color, fontWeight: 600 }}>{p.name}: {p.value}</Typography>)}
        </Box>
    );
};

const StatPill = ({ icon, label, value, color, sub }) => (
    <Box sx={{ flex: 1, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 1.5, textAlign: 'center' , ...glassCard}}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 26, fontWeight: 700, color: color ?? colors.primary, lineHeight: 1 }}>{value}</Typography>
        <Typography sx={{ fontSize: 10, color: colors.textFaint, mt: 0.3 }}>{label}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>{sub}</Typography>}
    </Box>
);

const ChartCard = ({ title, sub, children }) => (
    <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 2, ...glassCard }}>
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 700, color: colors.textPrimary, mb: sub ? 0.3 : 1.5 }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 1.5 }}>{sub}</Typography>}
        {children}
    </Box>
);

const BarberStats = () => {
    const { shopId, barberId } = useParams();
    const navigate = useNavigate();
    const [loading,  setLoading]  = useState(true);
    const [barber,   setBarber]   = useState(null);
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) navigate('/signin'); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!shopId || !barberId) return;
        const load = async () => {
            try {
                const barberSnap = await getDoc(doc(db, 'shops', shopId, 'barbers', barberId));
                if (barberSnap.exists()) setBarber({ id: barberSnap.id, ...barberSnap.data() });
                const from = new Date(); from.setDate(from.getDate() - 90); from.setHours(0,0,0,0);
                const q = query(collection(db, 'bookings'), where('barberId', '==', barberId), where('scheduledAt', '>=', Timestamp.fromDate(from)));
                const snap = await getDocs(q);
                setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, [shopId, barberId]);

    const today    = new Date(); today.setHours(0,0,0,0);
    const todayEnd = new Date(today); todayEnd.setDate(todayEnd.getDate() + 1);
    const inRange  = (b, from, to) => { const d = b.scheduledAt?.toDate?.(); return d >= from && d < to; };

    const todayBookings  = bookings.filter(b => inRange(b, today, todayEnd));
    const completed      = bookings.filter(b => b.status === 'completed').length;
    const cancelled      = bookings.filter(b => b.status === 'cancelled').length;
    const walkins        = bookings.filter(b => b.type === 'walkin').length;
    const appts          = bookings.filter(b => b.type === 'appointment').length;
    const completionRate = bookings.length > 0 ? Math.round((completed / bookings.length) * 100) : 0;
    const todayCompleted = todayBookings.filter(b => b.status === 'completed').length;
    const todayWaiting   = todayBookings.filter(b => ['waiting','pending'].includes(b.status)).length;
    const todayServing   = todayBookings.filter(b => b.status === 'serving').length;

    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today); d.setDate(d.getDate() - (6 - i));
        const dEnd = new Date(d); dEnd.setDate(dEnd.getDate() + 1);
        const dayB = bookings.filter(b => inRange(b, d, dEnd));
        return { day: DAY_LABELS[d.getDay()], total: dayB.length, done: dayB.filter(b => b.status === 'completed').length, walkins: dayB.filter(b => b.type === 'walkin').length };
    });

    const statusPie = [
        { name: 'Completed', value: completed,    color: colors.success    },
        { name: 'Cancelled', value: cancelled,    color: colors.error   },
        { name: 'Waiting',   value: todayWaiting, color: colors.primary },
        { name: 'Serving',   value: todayServing, color: colors.info },
    ].filter(d => d.value > 0);

    const hourlyData = Array.from({ length: 10 }, (_, i) => {
        const hour = 8 + i;
        const h = hour % 12 === 0 ? 12 : hour % 12;
        return { time: `${h}${hour < 12 ? 'AM' : 'PM'}`, count: todayBookings.filter(b => Math.floor((b.slotMinutes ?? 0) / 60) === hour).length };
    });

    const dayPerf = Array(7).fill(0);
    bookings.filter(b => b.status === 'completed').forEach(b => { const d = b.scheduledAt?.toDate?.(); if (d) dayPerf[d.getDay()]++; });
    const bestDay = DAY_LABELS[dayPerf.indexOf(Math.max(...dayPerf))];

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight: '100vh', color: colors.textPrimary, pb: 12 }}>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 100, bgcolor: colors.bgTopBar, borderBottom: `1px solid ${colors.borderLight}`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: colors.textMuted, p: 0.5 }}><ArrowBack fontSize="small" /></IconButton>
                {barber?.image && <Box component="img" src={barber.image} alt={barber.name} sx={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.border}` }} />}
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }}>{barber?.name ?? 'Barber'} — Stats</Typography>
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>Last 90 days</Typography>
                </Box>
            </Box>

            <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>Today</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <StatPill icon={<AccessTime sx={{ fontSize: 18, color: colors.primary }} />}  label="Waiting" value={todayWaiting}   color={colors.primary} />
                    <StatPill icon={<People sx={{ fontSize: 18, color: colors.info }} />}      label="Serving" value={todayServing}   color={colors.info} />
                    <StatPill icon={<CheckCircle sx={{ fontSize: 18, color: colors.success }} />}    label="Done"    value={todayCompleted} color={colors.success} />
                </Box>

                <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>Overall (90 days)</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <StatPill icon={<People sx={{ fontSize: 18, color: colors.primary }} />}      label="Total"     value={bookings.length} color={colors.primary} />
                    <StatPill icon={<CheckCircle sx={{ fontSize: 18, color: colors.success }} />}    label="Completed" value={completed}       color={colors.success} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <StatPill icon={<TrendingUp sx={{ fontSize: 18, color: colors.secondary }} />}       label="Walk-ins"     value={walkins} color={colors.secondary} />
                    <StatPill icon={<AccessTime sx={{ fontSize: 18, color: colors.textMuted }} />} label="Appointments" value={appts}   color={colors.textPrimary} />
                </Box>

                {bookings.length > 0 && (
                    <Box sx={{ bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: radius.md, px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, color: colors.success }} />
                        <Typography sx={{ fontSize: 12, color: colors.success }}>
                            Best day: <Typography component="span" sx={{ fontWeight: 700, fontSize: 12, color: colors.success }}>{bestDay}</Typography> — most completions
                        </Typography>
                    </Box>
                )}

                <ChartCard title="Bookings — Last 7 Days" sub="Total, completed, walk-ins">
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={last7Days} barGap={2} barCategoryGap="30%">
                            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                            <XAxis dataKey="day" tick={{ fill: colors.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.bg }} />
                            <Bar dataKey="total"   name="Total"     fill={colors.info} radius={[3,3,0,0]} />
                            <Bar dataKey="done"    name="Completed" fill={colors.success}    radius={[3,3,0,0]} />
                            <Bar dataKey="walkins" name="Walk-ins"  fill={colors.primary}      radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
                        {[[colors.info,'Total'],[colors.success,'Completed'],[colors.secondary,'Walk-ins']].map(([c,l]) => (
                            <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
                                <Typography sx={{ fontSize: 10, color: colors.textFaint }}>{l}</Typography>
                            </Box>
                        ))}
                    </Box>
                </ChartCard>

                <ChartCard title="Today — By Hour">
                    <ResponsiveContainer width="100%" height={150}>
                        <AreaChart data={hourlyData}>
                            <defs>
                                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={colors.primary} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                            <XAxis dataKey="time" tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: colors.primary, strokeWidth: 1 }} />
                            <Area type="monotone" dataKey="count" name="Bookings" stroke={colors.primary} fill="url(#areaGrad)" strokeWidth={2} dot={{ fill: colors.primary, r: 3 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                {statusPie.length > 0 && (
                    <ChartCard title="Status Breakdown">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <ResponsiveContainer width="50%" height={140}>
                                <PieChart>
                                    <Pie data={statusPie} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
                                        {statusPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {statusPie.map(d => (
                                    <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color }} />
                                        <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{d.name}</Typography>
                                        <Typography sx={{ fontSize: 12, color: d.color, fontWeight: 700, ml: 'auto' }}>{d.value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </ChartCard>
                )}

                {bookings.length > 0 && (
                    <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 2, ...glassCard }}>
                        <Typography sx={{ fontSize: 12, color: colors.textPrimary, mb: 1 }}>Completion Rate</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ fontFamily: typography.poppins, fontSize: 40, fontWeight: 700, color: colors.success }}>{completionRate}%</Typography>
                            <Box>
                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{completed} completed</Typography>
                                <Typography sx={{ fontSize: 12, color: colors.textFaintest }}>of {bookings.length} total</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 1.5, bgcolor: colors.border, borderRadius: 1, height: 6, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', borderRadius: 1, bgcolor: colors.success, width: `${completionRate}%`, transition: 'width 1s ease' }} />
                        </Box>
                    </Box>
                )}

                <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 2 , ...glassCard}}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 14, fontWeight: 700, color: colors.textPrimary, mb: 1.5 }}>Best Days</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {DAY_LABELS.map((day, i) => {
                            const maxVal = Math.max(...dayPerf, 1);
                            const pct    = dayPerf[i] / maxVal;
                            const isTop  = dayPerf[i] === Math.max(...dayPerf) && dayPerf[i] > 0;
                            return (
                                <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
                                    <Box sx={{ height: 50, borderRadius: 1.5, mb: 0.5, bgcolor: `rgba(0, 255, 0,${Math.max(pct*0.9,0.08)})`, border: `1px solid ${isTop ? colors.success : colors.border}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.5 }}>
                                        {isTop && <Typography sx={{ fontSize: 10 }}>⭐</Typography>}
                                    </Box>
                                    <Typography sx={{ fontSize: 10, color: isTop ? colors.success : colors.textPrimary }}>{day}</Typography>
                                    <Typography sx={{ fontSize: 9, color: colors.textPrimary }}>{dayPerf[i]}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {bookings.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ fontSize: 36, mb: 2 }}>📊</Typography>
                        <Typography sx={{ fontSize: 15, color: colors.textFaint }}>No data yet</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>Stats appear as you complete bookings</Typography>
                    </Box>
                )}
            </Box>
            <BarberBottomNav shopId={shopId} barberId={barberId} />
        </Box>
    );
};

export default BarberStats;